import React, { useEffect, useState } from 'react';
import { Table, Tag, Modal, Input, Switch, Card, Skeleton, Button } from 'antd';
import { toast } from '@rickylandino/react-messages';
import {
    getPendingSalesApprovals, reviewSalesApproval, getMyOooSettings, updateMyOooSettings
} from '../../services/SalesApprovalsService';

// The rep's note from the Change Sales Status modal is the `reason` field, stored
// inside the approval's serialized sales-order payload.
function reasonFromPayload(payloadJson) {
    try { return JSON.parse(payloadJson || '{}').reason || ''; }
    catch { return ''; }
}

export default function PendingSalesApprovals() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [denyModal, setDenyModal] = useState({ open: false, row: null, notes: '' });
    const [ooo, setOoo] = useState({ isOutOfOffice: false, altEmail: '' });

    function load() {
        setLoading(true);
        getPendingSalesApprovals().then(d => {
            setRows(Array.isArray(d) ? d : []);
            setLoading(false);
        }).catch(err => {
            // The API checks the live DB role, so it's the source of truth (sessionStorage
            // webcmRoles can be stale until the next login). A 403 means "not a manager".
            if (err && err.response && err.response.status === 403) setAccessDenied(true);
            else toast.error('Unable to load pending approvals');
            setLoading(false);
        });
    }

    useEffect(() => {
        load();
        getMyOooSettings().then(s => setOoo({ isOutOfOffice: !!s.isOutOfOffice, altEmail: s.altEmail || '' })).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function doApprove(row) {
        reviewSalesApproval(row.salesOrderApproval_ID, 'Approve', '').then(() => {
            toast.success('Approved — Customer Service has been notified');
            load();
        }).catch(() => toast.error('Could not approve'));
    }

    function submitDeny() {
        if (!denyModal.notes || !denyModal.notes.trim()) { toast.error('A reason is required to deny'); return; }
        reviewSalesApproval(denyModal.row.salesOrderApproval_ID, 'Deny', denyModal.notes).then(() => {
            toast.success('Denied — the rep has been emailed the reason');
            setDenyModal({ open: false, row: null, notes: '' });
            load();
        }).catch(() => toast.error('Could not deny'));
    }

    function saveOoo() {
        updateMyOooSettings(ooo.isOutOfOffice, ooo.altEmail).then(() => toast.success('Out-of-office settings saved'))
            .catch(() => toast.error('Could not save settings'));
    }

    if (accessDenied) {
        return <div className="text-muted p-3">You do not have access to sales approvals.</div>;
    }

    const columns = [
        { title: 'Exhibitor', dataIndex: 'exhibitorName', key: 'exhibitorName' },
        { title: 'Show', dataIndex: 'showName', key: 'showName' },
        { title: 'Requested by', dataIndex: 'requestedByUserId', key: 'requestedByUserId', width: 140 },
        {
            title: 'Free', key: 'free', width: 160,
            render: (_, r) => <>
                {r.fepFree && <Tag color="volcano">Featured Pkg</Tag>}
                {r.lrFree && <Tag color="gold">Lead Retrieval</Tag>}
            </>
        },
        { title: 'Note', key: 'note', ellipsis: true, render: (_, r) => reasonFromPayload(r.payloadJson) },
        { title: 'Requested', dataIndex: 'requestedAt', key: 'requestedAt', width: 170, render: d => d ? new Date(d).toLocaleString() : '' },
        {
            title: '', key: 'actions', width: 170,
            render: (_, r) => <>
                <button className="btn btn-sm btn-submit me-2" onClick={() => doApprove(r)}>Approve</button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => setDenyModal({ open: true, row: r, notes: '' })}>Deny</button>
            </>
        }
    ];

    return (
        <div className="p-3">
            <h4 className="mb-3">Pending Sales Approvals</h4>

            <Card size="small" title="Out of Office" className="mb-3" style={{ maxWidth: 520 }}>
                <div className="d-flex align-items-center mb-2">
                    <Switch checked={ooo.isOutOfOffice} onChange={v => setOoo({ ...ooo, isOutOfOffice: v })} />
                    <span className="ms-2">Redirect my approval alerts while I'm out</span>
                </div>
                <div className="d-flex align-items-center">
                    <span className="me-2">Alternate email:</span>
                    <Input style={{ maxWidth: 280 }} value={ooo.altEmail} placeholder="cover@d2p.com"
                        onChange={e => setOoo({ ...ooo, altEmail: e.target.value })} disabled={!ooo.isOutOfOffice} />
                    <Button className="ms-2" onClick={saveOoo}>Save</Button>
                </div>
            </Card>

            {loading ? <Skeleton active /> :
                <Table size="small" rowKey="salesOrderApproval_ID" columns={columns} dataSource={rows}
                    locale={{ emptyText: 'No sales orders are awaiting approval.' }}
                    pagination={rows.length > 20 ? { pageSize: 20 } : false} />}

            <Modal title="Deny sales order" visible={denyModal.open}
                onCancel={() => setDenyModal({ open: false, row: null, notes: '' })}
                footer={[
                    <button key="d" className="btn btn-outline-danger me-3" onClick={submitDeny}>Deny</button>,
                    <button key="c" className="btn btn-outline-primary" onClick={() => setDenyModal({ open: false, row: null, notes: '' })}>Cancel</button>
                ]}>
                <p>The reason below is emailed to the rep so they can research and re-submit (required):</p>
                <Input.TextArea rows={3} value={denyModal.notes}
                    onChange={e => setDenyModal({ ...denyModal, notes: e.target.value })} />
            </Modal>
        </div>
    );
}
